import React, { useState } from "react";
import {
  globalStyle,
  handleVisibleForEdit,
  handleDangerVisible,
} from "../../global/Utils";
import { updateUserList } from "../../Actions/UserAction";
import { useDispatch, useSelector } from "react-redux";
import { InputField } from "../Common/InputField";
import { useLocation, useNavigate } from "react-router-dom";
import { normalizeAppModes } from "./Variables";
import { AppModeSelect } from "./AppModeSelect";

import { upperCaseInput } from "../../global/textCase";
import { SearchableSelect } from "../Common/SearchableSelect";
const EditUser = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const formRef = React.useRef(null);
  const {
    _id,
    email,
    role,
    userName,
    allowCompanies,
    allowInvoices,
    expireDate,
    subscriptionInterval,
    appMode,
  } = location.state || {};
  const isUpdatingUserList = useSelector(
    (state) => state.UserReducer?.isUpdatingUserList,
  );
  const [formData, setFormData] = useState({
    email: email,
    password: "",
    role: role,
    userName: userName,
    allowCompanies: allowCompanies,
    allowInvoices: allowInvoices,
    expireDate: expireDate,
    subscriptionInterval: subscriptionInterval,
    appMode: normalizeAppModes(appMode),
  });
  const [errors, setErrors] = useState({});

  /* ------------------ HANDLE APP MODE CHANGE ------------------ */
  const handleAppModeChange = (modes) => {
    setFormData((prev) => ({ ...prev, appMode: modes }));
    setErrors((prev) => ({ ...prev, appMode: "" }));
  };

  /* ------------------ HANDLE INPUT CHANGE ------------------ */
  const handleChange = (e) => {
    const { name, value } = upperCaseInput(e.target);
    if (name === "allowInvoices" || name === "allowCompanies") {
      const newValue = Number(value);
      if (newValue < 0) return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  /* ------------------ VALIDATION ------------------ */
  const validate = () => {
    const newErrors = {};
    const requiredFields = [
      "email",
      "role",
      "allowCompanies",
      "allowInvoices",
      // "activeDate",
      "expireDate",
      "subscriptionInterval",
      "appMode",
    ];

    requiredFields.forEach((field) => {
      const value = formData[field];
      if (field === "allowCompanies" || field === "allowInvoices") {
        if (
          value === "" ||
          value === null ||
          value === undefined ||
          value === 0
        ) {
          newErrors[field] = "This field is required";
        } else if (Number(value) < 0) {
          newErrors[field] = "Value cannot be less than 0";
        }
      } else if (field === "appMode") {
        if (!value?.length) {
          newErrors[field] = "Select at least one app mode";
        }
      } else {
        if (!value || !value?.trim()) {
          newErrors[field] = "This field is required";
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ------------------ SUBMIT ------------------ */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) {
      setTimeout(() => {
        const firstError = document.querySelector(".border-red-500");
        if (firstError) {
          firstError.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          firstError.focus();
        }
      }, 100);
      return;
    }

    dispatch(
      updateUserList(
        _id,
        formData,
        handleVisibleForEdit,
        handleDangerVisible,
        navigate,
      ),
    );
  };
  return (
    <div className="w-full min-h-fit flex justify-center py-4">
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        autoComplete="off"
        className="w-full space-y-6"
      >
        <h2
          className="text-center font-bold text-xl"
          style={{
            fontFamily: globalStyle.fontFamilyHeading,
            fontSize: globalStyle.headingFontSizeSmall,
            color: globalStyle.curveDarkColor,
          }}
        >
          Edit User
        </h2>

        {/* Inputs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Email */}
          <InputField
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            error={errors.email}
            onChange={handleChange}
            required
          />
          {/* User Name */}
          <InputField
            label="User Name"
            name="userName"
            type="text"
            value={formData.userName}
            error={errors.userName}
            onChange={handleChange}
          />
          {/* Role */}
          <div>
            <label className="block mb-1 font-medium">
              Role <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              name="role"
              value={formData.role}
              onChange={handleChange}
              className={`uppercase w-full px-4 py-2 border rounded-md ${errors.role ? "border-red-500" : "border-gray-300"}`}
            >
              <option value="">Select</option>
              <option value="admin">Admin</option>
              <option value="customer">Customer</option>
            </SearchableSelect>
            {errors.role && (
              <p className="text-red-500 text-sm mt-1">{errors.role}</p>
            )}
          </div>
          {/* Allow Companies */}
          <InputField
            label="Allow Companies"
            name="allowCompanies"
            type="number"
            value={formData.allowCompanies}
            error={errors.allowCompanies}
            onChange={handleChange}
            required={true}
          />
          {/* Allow Invoices */}
          <InputField
            label="Allow Invoices"
            name="allowInvoices"
            type="number"
            value={formData.allowInvoices}
            error={errors.allowInvoices}
            onChange={handleChange}
            required={true}
          />
          {/* Active Date */}
          {/* <InputField
            label="Active Date"
            name="activeDate"
            type="date"
            min={today.toISOString().split("T")[0]}
            value={formData.activeDate}
            error={errors.activeDate}
            onChange={handleChange}
            required={true}
          /> */}
          {/* Expires Date */}
          <InputField
            label="Expires Date"
            name="expireDate"
            type="date"
            value={formData.expireDate}
            error={errors.expireDate}
            onChange={handleChange}
            required={true}
          />
          {/* Subscription Inerval */}
          <div>
            <label className="block mb-1 font-medium">
              Subscription Interval <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              name="subscriptionInterval"
              value={formData.subscriptionInterval}
              onChange={handleChange}
              className={`uppercase w-full px-4 py-2 border rounded-md ${
                errors.subscriptionInterval
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
            >
              <option value="">Select</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </SearchableSelect>
            {errors.subscriptionInterval && (
              <p className="text-red-500 text-sm mt-1">
                {errors.subscriptionInterval}
              </p>
            )}
          </div>
          {/* App Mode */}
          <AppModeSelect
            value={formData.appMode}
            error={errors.appMode}
            onChange={handleAppModeChange}
          />
        </div>

        {/* Submit button */}
        <div className="w-full flex justify-center">
          <button
            type="submit"
            error={errors.allowInvoices}
            onChange={handleChange}
            required={true}
          />
        </div>

        {/* Submit button */}
        <div className="w-full md:w-1/3 mx-auto flex gap-2">
          {/* Cancel Button */}
          <button
            type="button"
            onClick={() => navigate("/main/view-user-list")}
            className="w-full py-2 rounded-md font-bold border border-gray-400 text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>
          {/* Submit Button */}
          <button
            type="submit"
            disabled={isUpdatingUserList}
            className="w-full py-2 rounded-md text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: globalStyle.buttonColor }}
          >
            {isUpdatingUserList ? "Updating..." : "Update"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditUser;
